import { Flex, Icon, Spinner } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { TriggerType } from './TriggerType';
import { ScheduleTrigger } from './ScheduleTrigger';
import { ExistingTrigger } from './ExistingTrigger';
import type { IExistingTriggersListProps } from './types';

/**
 * ExistingTriggersList Component
 *
 * Fetches and displays existing triggers for an automation.
 * Fetches triggers from Grimoire with stage filter (DRAFT/PUBLISHED based on isDraft),
 * then fetches BDK trigger instances for additional details (connection, integration info).
 * Also includes the Manual trigger option.
 *
 * Layout:
 * [Manual Trigger] [Existing Triggers (if any)]
 *
 * @param props - Component props
 * @returns JSX element containing existing triggers and manual trigger option
 */
export function ExistingTriggersList({
  automationTriggers,
  isLoadingAutomationTriggers,
  inputSpecs,
  isDraft = true,
  automationId,
  isReadOnly = false,
  selectedTriggerId,
  isManualSelected,
  isScheduleSelected = false,
  hasSchedule,
  isScheduleEnabled,
  scheduleSummary,
}: IExistingTriggersListProps) {
  const { t } = useTranslation('automation-inputs');

  // Check if inputSpecs has any items
  const hasInputSpecs = inputSpecs && inputSpecs.length > 0;

  // Determine if manual trigger should be selected
  // Default to true when no invocationDetails provided (draft editing mode)
  const manualSelected = isManualSelected ?? true;

  // In read-only mode (run view) we surface only the trigger that actually
  // initiated the run, instead of the full editable list of options. Outside
  // read-only mode every option stays visible so users can pick/manage them.
  // The Manual card is an editor affordance (a way to run manually), not a real
  // fired trigger, so it's hidden entirely in the run view — for manual runs the
  // input values themselves are the content.
  const showManual = !isReadOnly;
  const showSchedule = !isReadOnly || isScheduleSelected;
  const visibleTriggers = isReadOnly
    ? automationTriggers.filter(
        ({ grimoireTrigger }) =>
          grimoireTrigger.trigger_id === selectedTriggerId,
      )
    : automationTriggers;

  return (
    <>
      {/* Manual trigger option - only show when there are inputSpecs */}
      {hasInputSpecs && showManual && (
        <TriggerType
          icon={
            <Icon
              type="TextCursorInput"
              size="lg"
              strokeWidth={1.5}
              className="text-ring"
            />
          }
          title={t('triggerTypes.manual.title')}
          description={t('triggerTypes.manual.description')}
          isSelected={manualSelected}
        />
      )}

      {hasSchedule && !isDraft && showSchedule && (
        <ScheduleTrigger
          automationId={automationId}
          hasSchedule={hasSchedule}
          isEnabled={isScheduleEnabled}
          description={
            scheduleSummary ?? t('triggerTypes.schedule.description')
          }
        />
      )}

      {/* Loading state */}
      {isLoadingAutomationTriggers ? (
        <Flex align="center" justify="center" className="h-40 w-35">
          <Spinner size="sm" />
        </Flex>
      ) : (
        <>
          {/* Existing Triggers for this automation */}
          {visibleTriggers.map(({ grimoireTrigger, bdkInstance }) => (
            <ExistingTrigger
              isDraft={isDraft}
              key={grimoireTrigger.trigger_id}
              triggerInstance={bdkInstance}
              grimoireTriggerInfo={grimoireTrigger}
              inputSpecs={inputSpecs}
              automationId={automationId}
              isReadOnly={isReadOnly}
              isSelected={selectedTriggerId === grimoireTrigger.trigger_id}
            />
          ))}
        </>
      )}
    </>
  );
}
